import { ReactElement } from 'react';

import Box from '@mui/material/Box';
import { SxProps, Theme, useThemeProps } from '@mui/material/styles';
import Typography from '@mui/material/Typography';

import { getContrastingColor } from '../../utils/colorUtils';

export type GratiMarkerShape = 'circle' | 'square' | 'shield';
export type GratiMarkerSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

const SIZE_MAP: Record<GratiMarkerSize, { box: string; font: string }> = {
  xs: { box: '0.75rem',  font: '0.4rem'   },
  sm: { box: '0.875rem', font: '0.45rem'  },
  md: { box: '1.5rem',   font: '0.75rem'  },
  lg: { box: '2rem',     font: '0.875rem' },
  xl: { box: '2.5rem',   font: '1rem'     },
};

// Classic heater-shield: rectangular body tapering to a point at the bottom.
const SHIELD_CLIP = 'polygon(0 0, 100% 0, 100% 70%, 50% 100%, 0 70%)';

export interface GratiMarkerProps {
  shape?: GratiMarkerShape;
  /** Primary fill color. Defaults to neutral gray when omitted. */
  color?: string;
  /** Second color for the 'square' shape combo-tee diagonal split. */
  color2?: string;
  /** Text rendered inside the marker. */
  label?: string;
  /** Named size or a raw CSS dimension string (e.g. '1.25rem'). Default: 'md'. */
  size?: GratiMarkerSize | string;
  /** Reduces opacity to 0.5, indicating a placeholder or draft state. */
  faded?: boolean;
  /** A dashed outline around the marker — e.g. a tee the player picked themselves. */
  dashed?: boolean;
  onClick?: (event: React.MouseEvent<HTMLElement>) => void;
  sx?: SxProps<Theme>;
}

export default function GratiMarker(inProps: GratiMarkerProps): ReactElement {
  const { shape = 'circle', color = '#bbb', color2, label, size = 'md', faded = false, dashed = false, onClick, sx } =
    useThemeProps({ props: inProps, name: 'GratiMarker' });
  const sizeEntry = SIZE_MAP[size as GratiMarkerSize];
  const boxSize = sizeEntry ? sizeEntry.box : (size as string);
  const fontSize = sizeEntry ? sizeEntry.font : '0.5em';
  const textColor = label ? getContrastingColor(color) : undefined;

  const background =
    shape === 'square' && color2
      ? `linear-gradient(135deg, ${color} 50%, ${color2} 50%)`
      : color;

  return (
    <Box
      onClick={onClick}
      sx={{
        width: boxSize,
        height: boxSize,
        flexShrink: 0,
        ...(shape === 'square'
          ? { background, border: '1px solid', borderColor: 'divider' }
          : { backgroundColor: color }),
        borderRadius: shape === 'circle' ? '50%' : shape === 'square' ? '2px' : undefined,
        clipPath: shape === 'shield' ? SHIELD_CLIP : undefined,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        ...(shape === 'shield' && { paddingBottom: '0.15rem' }),
        opacity: faded ? 0.5 : 1,
        ...(dashed && { outline: '1px dashed', outlineColor: 'text.primary', outlineOffset: '2px' }),
        cursor: onClick ? 'pointer' : 'default',
        '&:hover': onClick ? { opacity: faded ? 0.4 : 0.8 } : {},
        ...sx,
      }}
    >
      {label && (
        <Typography sx={{ fontSize, fontWeight: 700, color: textColor, lineHeight: 1, userSelect: 'none' }}>
          {label}
        </Typography>
      )}
    </Box>
  );
}
